<template>
  <div class="export-table">
    <data-table
      :headers="headers"
      :items="exports"
      :loading="loading"
      :items-per-page="itemsPerPage"
      :hide-default-footer="exports.length <= itemsPerPage"
      class="elevation-0"
      :aria-label="'Data exports table'"
      :data-testid="'export-table'"
      :disable-sort="true"
      :interactable="false"
    >
      <!-- Loading slot -->
      <template #progress>
        <v-progress-linear
          indeterminate
          color="primary"
          aria-live="polite"
          aria-label="Loading export data"
        />
      </template>

      <!-- Name column (stacked with location) -->
      <template #item.exportName="{ item }">
        <table-cell-stacked
          :primary="item.exportName"
          :secondary="item.locationName"
        />
      </template>

      <!-- Enabled column -->
      <template #item.enabled="{ item }">
        <div class="d-flex align-center justify-center">
          <status-badge
            :status="item.enabled ? 'Enabled' : 'Disabled'"
            :type="item.enabled ? 'success' : 'info'"
            :aria-label="`Export status: ${item.enabled ? 'Enabled' : 'Disabled'}`"
          />
        </div>
      </template>

      <!-- Frequency column -->
      <template #item.frequency="{ item }">
        <div class="d-flex align-center justify-center">
          <export-frequency-chip :frequency="item.frequency" />
        </div>
      </template>

      <!-- Last Export column (stacked with relative time) -->
      <template #item.lastExportDate="{ item }">
        <div class="d-flex align-center justify-center">
          <div v-if="item.lastExportDate" class="last-export-cell">
            <div class="last-export-cell__date">
              {{ formatDate(item.lastExportDate) }}
            </div>
            <div class="last-export-cell__relative">
              {{ getRelativeTime(item.lastExportDate) }}
            </div>
          </div>
          <span v-else class="text-body-2 muted-text">
            Never run
          </span>
        </div>
      </template>

      <!-- Result column -->
      <template #item.lastExportResult="{ item }">
        <div class="d-flex align-center justify-center">
          <status-badge
            :status="getResultStatus(item.lastExportResult)"
            :type="getResultType(item.lastExportResult)"
            :aria-label="`Export result: ${getResultStatus(item.lastExportResult)}`"
          />
        </div>
      </template>

      <!-- Next Run column -->
      <template #item.nextRunDate="{ item }">
        <div class="d-flex align-center justify-center">
          <export-next-run-cell :next-run-date="item.nextRunDate" />
        </div>
      </template>

      <!-- Actions column -->
      <template #item.actions="{ item }">
        <div class="d-flex align-center justify-center gap-2">
          <v-btn
            small
            color="primary"
            :disabled="!item.enabled"
            @click="handleRunNow(item)"
            aria-label="Run export now"
          >
            <v-icon small class="mr-1">mdi-play</v-icon>
            Run Now
          </v-btn>
          <v-btn
            small
            outlined
            @click="handleEdit(item)"
            aria-label="Edit export"
          >
            <v-icon small class="mr-1">mdi-pencil</v-icon>
            Edit
          </v-btn>
          <v-menu offset-y left>
            <template #activator="{ on, attrs }">
              <v-btn
                icon
                small
                v-bind="attrs"
                v-on="on"
                aria-label="More actions"
              >
                <v-icon small>mdi-dots-vertical</v-icon>
              </v-btn>
            </template>
            <v-list dense>
              <v-list-item @click="handleViewHistory(item)">
                <v-list-item-icon>
                  <v-icon small>mdi-history</v-icon>
                </v-list-item-icon>
                <v-list-item-content>
                  <v-list-item-title>View History</v-list-item-title>
                </v-list-item-content>
              </v-list-item>
              <v-list-item @click="handleClone(item)">
                <v-list-item-icon>
                  <v-icon small>mdi-content-copy</v-icon>
                </v-list-item-icon>
                <v-list-item-content>
                  <v-list-item-title>Clone</v-list-item-title>
                </v-list-item-content>
              </v-list-item>
              <v-divider />
              <v-list-item @click="handleDelete(item)" class="error--text">
                <v-list-item-icon>
                  <v-icon small color="error">mdi-delete</v-icon>
                </v-list-item-icon>
                <v-list-item-content>
                  <v-list-item-title>Delete</v-list-item-title>
                </v-list-item-content>
              </v-list-item>
            </v-list>
          </v-menu>
        </div>
      </template>

      <!-- Empty state -->
      <template #no-data>
        <div class="text-center py-8">
          <v-icon size="48" class="muted-icon">mdi-database-export-outline</v-icon>
          <div class="text-h6 muted-text mt-4">No exports found</div>
          <div class="text-body-2 muted-text">Exports will appear here once they are configured</div>
        </div>
      </template>
    </data-table>
  </div>
</template>

<script lang="ts">
import Vue from 'vue'
import type { PropType } from 'vue'
import DataTable from '../DataTable/DataTable.vue'
import StatusBadge from '../StatusBadge/StatusBadge.vue'
import ExportNextRunCell from './ExportNextRunCell.vue'
import ExportFrequencyChip from './ExportFrequencyChip.vue'
import TableCellStacked from '../DataTable/cells/TableCellStacked.vue'
import type { DataExport } from './types'
import { formatDate } from '../../utils/formatters'

export default Vue.extend({
  name: 'ExportTable',

  components: {
    DataTable,
    ExportFrequencyChip,
    ExportNextRunCell,
    TableCellStacked,
    StatusBadge
  },

  props: {
    /** Array of data exports */
    exports: {
      type: Array as PropType<DataExport[]>,
      required: true
    },
    /** Loading state */
    loading: {
      type: Boolean,
      default: false
    },
    /** Items per page */
    itemsPerPage: {
      type: Number,
      default: 10
    }
  },

  data() {
    return {
      headers: [
        {
          text: 'Name',
          value: 'exportName',
          sortable: false
        },
        {
          text: 'Enabled',
          value: 'enabled',
          sortable: false,
          width: '120px'
        },
        {
          text: 'Frequency',
          value: 'frequency',
          sortable: false,
          width: '140px'
        },
        {
          text: 'Last Export',
          value: 'lastExportDate',
          sortable: false,
          width: '200px'
        },
        {
          text: 'Result',
          value: 'lastExportResult',
          sortable: false,
          width: '120px'
        },
        {
          text: 'Next Run',
          value: 'nextRunDate',
          sortable: false,
          width: '200px'
        },
        {
          text: 'Actions',
          value: 'actions',
          sortable: false,
          align: 'center' as const,
          width: '280px'
        }
      ]
    }
  },

  methods: {
    formatDate,
    getResultStatus(result: string | null): string {
      if (!result) {
        return 'Pending'
      }

      const resultLower = result.toLowerCase()

      if (
        resultLower.includes('success') ||
        resultLower.includes('complete') ||
        resultLower.includes('exported')
      ) {
        return 'Success'
      }

      if (
        resultLower.includes('error') ||
        resultLower.includes('fail') ||
        resultLower.includes('exception')
      ) {
        return 'Failed'
      }

      return 'Pending'
    },

    getResultType(result: string | null): 'success' | 'error' | 'info' {
      if (!result) {
        return 'info'
      }

      const resultLower = result.toLowerCase()

      if (
        resultLower.includes('success') ||
        resultLower.includes('complete') ||
        resultLower.includes('exported')
      ) {
        return 'success'
      }

      if (
        resultLower.includes('error') ||
        resultLower.includes('fail') ||
        resultLower.includes('exception')
      ) {
        return 'error'
      }

      return 'info'
    },



    getRelativeTime(dateString: string): string {
      const now = new Date()
      const date = new Date(dateString)
      const diffMs = now.getTime() - date.getTime()

      const diffMinutes = Math.floor(diffMs / (1000 * 60))
      const diffHours = Math.floor(diffMs / (1000 * 60 * 60))
      const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24))

      if (diffMinutes < 1) {
        return 'just now'
      } else if (diffMinutes < 60) {
        return `${diffMinutes}m ago`
      } else if (diffHours < 24) {
        return `${diffHours}h ago`
      } else if (diffDays === 1) {
        return '1 day ago'
      } else {
        return `${diffDays} days ago`
      }
    },

    handleRunNow(item: DataExport) {
      this.$emit('run-now', item)
    },

    handleEdit(item: DataExport) {
      this.$emit('edit', item)
    },

    handleViewHistory(item: DataExport) {
      this.$emit('view-history', item)
    },

    handleClone(item: DataExport) {
      this.$emit('clone', item)
    },

    handleDelete(item: DataExport) {
      this.$emit('delete', item)
    }
  }
})
</script>

<style scoped>
.export-table {
  background-color: white;
  border-radius: var(--radius);
  overflow: hidden;
}

.export-table >>> .v-data-table {
  border: 1px solid rgba(0, 0, 0, 0.12);
  border-radius: var(--radius);
}

/* Header styling */
.export-table >>> .v-data-table thead th {
  font-weight: 600;
  font-size: 0.75rem;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  text-align: center !important;
  padding: 16px 16px !important;
  height: 56px !important;
  vertical-align: middle !important;
  background-color: white !important;
}

/* Header text alignment */
.export-table >>> .v-data-table thead th span {
  display: block;
  text-align: center !important;
}

/* Row hover */
.export-table >>> .v-data-table tbody tr:hover {
  background-color: rgba(0, 0, 0, 0.02) !important;
}

/* Cell styling */
.export-table >>> .v-data-table tbody td {
  padding: 16px 16px !important;
  height: auto !important;
  min-height: 72px;
  vertical-align: middle !important;
}

.export-table >>> .v-data-table tbody tr {
  min-height: 72px;
}

/* Last export cell styling */
.last-export-cell {
  display: flex;
  flex-direction: column;
  gap: 4px;
  padding: 4px 0;
}

.last-export-cell__date {
  font-size: 0.875rem;
  font-weight: 500;
  line-height: 1.4;
  color: var(--foreground);
}

.last-export-cell__relative {
  font-size: 0.8125rem;
  color: var(--muted-foreground);
  line-height: 1.3;
}

/* Muted text and icon utilities */
.muted-text {
  color: var(--muted-foreground) !important;
}

.muted-icon {
  color: var(--muted-foreground) !important;
}

/* Action button spacing */
.gap-2 {
  gap: 8px;
}
</style>
